import { StyleSheet, css } from 'aphrodite';
import { type FC } from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { getFormattedPhoneNumber } from '../../../models/utils/phoneUtils';
import type {
	CellPhoneLookupConfidence,
	ICellPhoneLookupMatch,
	ICellPhoneLookupResult,
} from '../../queries/CellPhoneLookup';

/**
 * Cell-phone picker dialog. Mirrors the legacy `CellPhoneLookupModal` but
 * trimmed to the LEFT-column needs:
 *
 *   - Lists every match with confidence + source/phone-type labels.
 *   - "Add to contact" appends the chosen number via `onAddNumber`.
 *   - "Mark wrong" hands off to `onMarkWrong` (the calling site owns the
 *     `useMarkWrongNumberMutation`).
 *
 * The component is purely presentational. The lookup itself runs in
 * `FocusedLeadView` via `useFindCellPhone`; this dialog just shows what came
 * back. State for which numbers were already added, which are pending, and
 * which were marked wrong is owned by the caller too — that way the parent
 * controls re-fetch / cache invalidation.
 */
export type CellPhonePickerDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contactName: string;
	result: ICellPhoneLookupResult | undefined;
	loading: boolean;
	error?: string;
	onRetry: () => void;
	onAddNumber: (match: ICellPhoneLookupMatch) => void;
	onMarkWrong: (match: ICellPhoneLookupMatch) => void;
	addedNumbers: Set<string>;
	pendingNumber?: string | null;
	markWrongPendingNumber?: string | null;
};

// Match the legacy CellPhoneLookupModal's confidence palette. Resolved here
// rather than inline so the JSX stays readable.
const confidenceStyleFor = (s: typeof styles, confidence: CellPhoneLookupConfidence) => {
	switch (confidence) {
		case 'High':
			return s.confidenceHigh;
		case 'MediumHigh':
			return s.confidenceMediumHigh;
		case 'Medium':
			return s.confidenceMedium;
		default:
			return s.confidenceUnresolved;
	}
};

export const CellPhonePickerDialog: FC<CellPhonePickerDialogProps> = ({
	open,
	onOpenChange,
	contactName,
	result,
	loading,
	error,
	onRetry,
	onAddNumber,
	onMarkWrong,
	addedNumbers,
	pendingNumber,
	markWrongPendingNumber,
}) => {
	const s = styles;
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[s.popup]}>
				<DialogHeader>
					<DialogTitle>Cell phone lookup: {contactName}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					{loading ? (
						<div className={css(s.empty)}>Searching for cell phone numbers…</div>
					) : error ? (
						<div className={css(s.empty)}>
							<div className={css(s.errorText)}>Lookup failed.</div>
							<Button variant='outline' size='sm' onClick={onRetry}>
								Retry
							</Button>
						</div>
					) : !result || result.status !== 'Completed' || result.matches.length === 0 ? (
						<div className={css(s.empty)}>No cell phone numbers found for this contact.</div>
					) : (
						<div className={css(s.list)}>
							{result.matches
								.filter(m => !!m.phoneNumber)
								.map((match, i) => {
									const isAdded = addedNumbers.has(match.phoneNumber);
									const isAddPending = pendingNumber === match.phoneNumber;
									const isMarkWrongPending = markWrongPendingNumber === match.phoneNumber;
									const displayNumber = getFormattedPhoneNumber(match.phoneNumber.replace(/^\+1/, ''));
									return (
										<div key={`${match.phoneNumber}-${i}`} className={css(s.row)}>
											<div className={css(s.rowMain)}>
												<div className={css(s.rowHead)}>
													<span className={css(s.phoneNumber)}>{displayNumber}</span>
													{match.isMarkedWrong ? (
														<span className={css(s.wrongBadge)}>Wrong number</span>
													) : (
														<span className={css(s.confidenceBase, confidenceStyleFor(s, match.confidence))}>
															{match.confidence}
														</span>
													)}
												</div>
												{!!match.matchedName && <div className={css(s.matchedName)}>Matched: {match.matchedName}</div>}
												<div className={css(s.metaRow)}>
													{!!match.phoneType && <span className={css(s.metaLabel)}>{match.phoneType}</span>}
													{match.isConnected === false && (
														<span className={css(s.disconnectedBadge)}>Disconnected</span>
													)}
												</div>
												{match.confidenceReasons?.length > 0 && (
													<div className={css(s.reasons)}>{match.confidenceReasons.join(', ')}</div>
												)}
												{!!match.notes && <div className={css(s.notes)}>{match.notes}</div>}
											</div>
											<div className={css(s.rowActions)}>
												{isAdded ? (
													<span className={css(s.addedBadge)}>Added</span>
												) : match.isMarkedWrong ? null : (
													<>
														<Button
															variant='outline'
															size='sm'
															disabled={isMarkWrongPending}
															onClick={() => onMarkWrong(match)}
														>
															{isMarkWrongPending ? 'Marking…' : 'Mark wrong'}
														</Button>
														<Button
															variant='primary'
															size='sm'
															disabled={isAddPending}
															onClick={() => onAddNumber(match)}
														>
															{isAddPending ? 'Adding…' : 'Add to contact'}
														</Button>
													</>
												)}
											</div>
										</div>
									);
								})}
						</div>
					)}
				</DialogBody>
				<DialogFooter>
					<Button variant='outline' onClick={() => onOpenChange(false)}>
						Close
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	popup: {
		maxWidth: 560,
	},
	empty: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 12,
		padding: '24px 8px',
		fontSize: 13,
		color: 'var(--ink-muted)',
	},
	errorText: {
		color: 'var(--ink)',
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
	},
	row: {
		display: 'flex',
		gap: 12,
		padding: 12,
		border: '1px solid var(--border)',
		borderRadius: 8,
		background: 'var(--bg-elev)',
	},
	rowMain: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	rowHead: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	rowActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		flexShrink: 0,
	},
	phoneNumber: {
		fontSize: 14,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	confidenceBase: {
		fontSize: 11,
		fontWeight: 600,
		padding: '2px 8px',
		borderRadius: 999,
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	confidenceHigh: {
		background: 'rgba(16, 163, 127, 0.15)',
		color: '#10A37F',
	},
	confidenceMediumHigh: {
		background: 'rgba(59, 130, 246, 0.15)',
		color: '#3B82F6',
	},
	confidenceMedium: {
		background: 'rgba(245, 158, 11, 0.15)',
		color: '#F59E0B',
	},
	confidenceUnresolved: {
		background: 'var(--bg-sunken)',
		color: 'var(--ink-muted)',
	},
	wrongBadge: {
		fontSize: 11,
		fontWeight: 600,
		padding: '2px 8px',
		borderRadius: 999,
		background: 'rgba(239, 68, 68, 0.15)',
		color: '#EF4444',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	matchedName: {
		fontSize: 12,
		color: 'var(--ink-muted)',
	},
	metaRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 11,
		color: 'var(--ink-faint)',
	},
	metaLabel: {
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	disconnectedBadge: {
		color: '#EF4444',
		fontWeight: 600,
	},
	reasons: {
		fontSize: 11,
		color: 'var(--ink-faint)',
	},
	notes: {
		fontSize: 11,
		color: 'var(--ink-muted)',
		fontStyle: 'italic',
	},
	addedBadge: {
		fontSize: 11,
		fontWeight: 600,
		padding: '4px 10px',
		borderRadius: 999,
		background: 'rgba(16, 163, 127, 0.15)',
		color: '#10A37F',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
});
